// src/utils/sampleOrderVisibility.js
// Centralized visibility rules for sample orders (staff + customers)
//
// ✅ NEW STANDARD: sampleOrders identity uses createdBy / createdByName / createdByRole
//    - NO requestedBy
//    - NO requestedById / requestedByUid / requestedByName / requestedByRole
//    - NO legacy fallbacks for requester identity
//
// Staff visibility (owner/admin see all):
// - createdBy == me
// - assignedToId == me
// - NO broad group-overlap fallback for end users
//
// Customer visibility:
// - accountId == myCustomerAccountId OR createdBy == me
//
// NOTE: This is UI filtering ONLY. Firestore rules must also enforce visibility.

const norm = (v) => (v == null ? "" : String(v).trim());

export const normalizeRole = (role) =>
  norm(role).toLowerCase().replace(/[_\s-]+/g, "");

export const isOwnerOrAdminRole = (role) => {
  const r = normalizeRole(role);
  return r === "owner" || r === "admin";
};

export const isCustomerRole = (role) => normalizeRole(role) === "customer";

export const isEndUserRole = (role) => normalizeRole(role) === "enduser";

const normId = (v) => {
  if (v == null) return null;
  if (typeof v === "string") return v.trim() || null;
  if (typeof v === "number") return String(v);
  if (typeof v === "object") {
    return (
      (typeof v.id === "string" && v.id.trim()) ||
      (typeof v.uid === "string" && v.uid.trim()) ||
      (typeof v.userId === "string" && v.userId.trim()) ||
      (typeof v.value === "string" && v.value.trim()) ||
      null
    );
  }
  const s = String(v).trim();
  return s || null;
};

const getUserIdFromOrder = (order, fieldCandidates) => {
  for (const f of fieldCandidates) {
    const val = normId(order?.[f]);
    if (val) return val;
  }
  return null;
};

export const canUserSeeSampleOrder = (
  order,
  { userId, role, groupIds = [], customerAccountId = null, visibleAccountIds = [] } = {},
  { includeHidden = false } = {}
) => {
  if (!order) return false;

  // Hidden handling
  if (order?.hidden && !includeHidden) return false;

  // Admin/Owner: everything (non-hidden handled above)
  if (isOwnerOrAdminRole(role)) return true;

  // Must have a userId for non-admin checks
  const me = normId(userId);
  if (!me) return false;

  // ✅ NEW STANDARD identity fields ONLY
  const createdBy = getUserIdFromOrder(order, ["createdBy"]) || null;

  // assignment can still accept a few shapes, but it's NOT requester identity
  const assignedToId =
    getUserIdFromOrder(order, ["assignedToId", "assignedTo", "assignedUserId"]) || null;
  const orderAccountId = normId(order?.accountId) || null;
  const orderLocationId = normId(order?.locationId) || null;
  const orderAccountIds = Array.isArray(order?.accountIds)
    ? order.accountIds.map(normId).filter(Boolean)
    : [];
  const visibleAccountIdSet = new Set(
    (Array.isArray(visibleAccountIds) ? visibleAccountIds : []).map(normId).filter(Boolean)
  );

  // Customer rules
  if (isCustomerRole(role)) {
    const myAcct = normId(customerAccountId) || null;

    return (
      (myAcct && orderAccountId && orderAccountId === myAcct) ||
      (myAcct && orderLocationId && orderLocationId === myAcct) ||
      (myAcct && orderAccountIds.includes(myAcct)) ||
      (createdBy && createdBy === me)
    );
  }

  // Staff (endUser) rules: mine (created/assigned/account-linked)
  if (createdBy && createdBy === me) return true;
  if (assignedToId && assignedToId === me) return true;
  if (orderAccountId && visibleAccountIdSet.has(orderAccountId)) return true;
  if (orderLocationId && visibleAccountIdSet.has(orderLocationId)) return true;
  if (orderAccountIds.some((id) => visibleAccountIdSet.has(id))) return true;

  return false;
};

export const filterSampleOrdersForUser = (orders = [], visibilityContext = {}, options = {}) => {
  const arr = Array.isArray(orders) ? orders : [];
  return arr.filter((o) => canUserSeeSampleOrder(o, visibilityContext, options));
};

/**
 * ✅ Backwards-compatible wrapper for older components (Table/Kanban)
 * Supports calls like:
 *   filterVisibleSampleOrders(requests, {
 *     isAdmin,
 *     currentUserId,
 *     currentUserRole,
 *     currentUserGroupIds,
 *     includeHidden,
 *     currentUserAccountId
 *   })
 *
 * ✅ Still enforces NEW STANDARD (createdBy only; no requestedBy)
 */
export const filterVisibleSampleOrders = (orders = [], ctx = {}, opts = {}) => {
  const arr = Array.isArray(orders) ? orders : [];

  const {
    // new-style
    userId,
    role,
    groupIds,
    customerAccountId,

    // old-style (Table/Kanban)
    isAdmin,
    currentUserId,
    currentUserRole,
    currentUserGroupIds,
    currentUserAccountId,
    includeHidden,
  } = ctx || {};

  const effectiveUserId = normId(userId || currentUserId || null);

  let effectiveRole = role || currentUserRole || "";
  if (isAdmin && !isOwnerOrAdminRole(effectiveRole)) {
    // treat "isAdmin" as admin for visibility purposes
    effectiveRole = "admin";
  }

  const effectiveGroupIds = Array.isArray(groupIds)
    ? groupIds
    : Array.isArray(currentUserGroupIds)
    ? currentUserGroupIds
    : [];

  const effectiveCustomerAccountId = customerAccountId || currentUserAccountId || null;

  let includeHiddenFinal = !!(opts && opts.includeHidden);
  if (typeof includeHidden === "boolean") includeHiddenFinal = includeHidden;

  return filterSampleOrdersForUser(
    arr,
    {
      userId: effectiveUserId,
      role: effectiveRole,
      groupIds: effectiveGroupIds,
      customerAccountId: effectiveCustomerAccountId,
      visibleAccountIds:
        Array.isArray(ctx?.visibleAccountIds)
          ? ctx.visibleAccountIds
          : Array.isArray(ctx?.currentUserAccountIds)
          ? ctx.currentUserAccountIds
          : [],
    },
    { includeHidden: includeHiddenFinal }
  );
};
